import React from "react";
import styled from "styled-components";
import {
  FiLayers,
  FiLayout,
  FiServer,
  FiDatabase,
  FiArrowUpRight,
  FiCheck,
  FiGlobe,
  FiSmartphone,
  FiCode,
  FiSend,
} from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  z-index: 1;
  align-items: center;
  padding: 60px 0 80px 0;
  @media (max-width: 960px) {
    padding: 40px 0 60px 0;
  }
`;

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 1150px;
  gap: 16px;
  padding: 0 20px;
`;

const Tag = styled.div`
  background: rgba(133, 76, 230, 0.12);
  border: 1px solid rgba(133, 76, 230, 0.4);
  color: #c084fc;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  padding: 6px 18px;
  border-radius: 9999px;
  box-shadow: 0 0 15px rgba(133, 76, 230, 0.2);
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const Title = styled.h2`
  font-size: 38px;
  font-weight: 700;
  text-align: center;
  color: ${({ theme }) => theme.text_primary};
  margin: 0;
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    font-size: 30px;
  }
`;

const SubTitle = styled.p`
  font-size: 16px;
  text-align: center;
  max-width: 650px;
  color: rgba(255, 255, 255, 0.65);
  line-height: 1.6;
  margin: 0 0 20px 0;

  @media (max-width: 768px) {
    font-size: 14.5px;
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  width: 100%;

  @media (max-width: 840px) {
    grid-template-columns: 1fr;
    gap: 18px;
  }
`;

const ServiceCard = styled.div`
  background: ${({ theme }) => theme.card || "#171721"};
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 20px;
  padding: 34px 30px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  transition: all 0.35s ease;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.25);
  position: relative;
  overflow: hidden;

  &:hover {
    border-color: rgba(133, 76, 230, 0.5);
    transform: translateY(-5px);
    box-shadow: 0 10px 32px rgba(133, 76, 230, 0.15);
  }

  @media (max-width: 640px) {
    padding: 26px 20px;
  }
`;

const IconBox = styled.div`
  width: 46px;
  height: 46px;
  border-radius: 12px;
  background: rgba(133, 76, 230, 0.15);
  border: 1px solid rgba(133, 76, 230, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c084fc;
  font-size: 22px;
  margin-bottom: 4px;
  box-shadow: 0 0 15px rgba(133, 76, 230, 0.2);
`;

const CardTitle = styled.h3`
  font-size: 21px;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
  letter-spacing: -0.01em;

  @media (max-width: 640px) {
    font-size: 19px;
  }
`;

const CardDescription = styled.p`
  font-size: 14.5px;
  line-height: 1.65;
  color: rgba(255, 255, 255, 0.65);
  margin: 0;
  flex-grow: 1;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 6px;
`;

const TagBadge = styled.span`
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.65);
  letter-spacing: 0.02em;
  transition: all 0.2s ease;

  ${ServiceCard}:hover & {
    border-color: rgba(133, 76, 230, 0.25);
    color: rgba(255, 255, 255, 0.85);
  }
`;

/* ---------------------------------
   CLIENT PROJECT & STARTUP PROMO CARD
--------------------------------- */
const ClientServicesBanner = styled.div`
  width: 100%;
  margin-top: 8px;
  margin-bottom: 28px;
  background: linear-gradient(
    135deg,
    rgba(133, 76, 230, 0.16) 0%,
    rgba(190, 26, 219, 0.1) 50%,
    rgba(56, 189, 248, 0.06) 100%
  ),
  ${({ theme }) => theme.card || "#171721"};
  border: 1px solid rgba(133, 76, 230, 0.45);
  border-radius: 24px;
  padding: 38px 34px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
  transition: all 0.35s ease;

  &:hover {
    border-color: rgba(133, 76, 230, 0.8);
    box-shadow: 0 16px 48px rgba(133, 76, 230, 0.22);
  }

  @media (max-width: 640px) {
    padding: 26px 20px;
  }
`;

const PromoHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PromoBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(133, 76, 230, 0.2);
  border: 1px solid rgba(133, 76, 230, 0.5);
  color: #e879f9;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  padding: 6px 16px;
  border-radius: 9999px;
  width: fit-content;
`;

const PromoTitle = styled.h3`
  font-size: clamp(1.6rem, 3.2vw, 2.3rem);
  font-weight: 800;
  color: #ffffff;
  margin: 0;
  line-height: 1.25;
  letter-spacing: -0.02em;

  span {
    background: linear-gradient(135deg, #854CE6 0%, #c026d3 50%, #38bdf8 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const PromoDescription = styled.p`
  font-size: 15px;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.75);
  margin: 0;
  max-width: 860px;

  strong {
    color: #ffffff;
    font-weight: 600;
  }
`;

/* 4 Client offerings mini-grid */
const ClientOfferingsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: 14px;
  width: 100%;
`;

const OfferingBox = styled.div`
  background: rgba(255, 255, 255, 0.035);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  transition: all 0.25s ease;

  &:hover {
    background: rgba(133, 76, 230, 0.12);
    border-color: rgba(133, 76, 230, 0.4);
    transform: translateY(-2px);
  }
`;

const OfferingTop = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 15px;
  font-weight: 700;
  color: #ffffff;

  svg {
    color: #38bdf8;
    font-size: 18px;
    flex-shrink: 0;
  }
`;

const OfferingDesc = styled.p`
  font-size: 13px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.6);
  margin: 0;
`;

const PromoActions = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  margin-top: 6px;
`;

const ContactButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #854CE6 0%, #a855f7 100%);
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.04em;
  padding: 13px 24px;
  border-radius: 12px;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(133, 76, 230, 0.35);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 28px rgba(133, 76, 230, 0.55);
    filter: brightness(1.1);
  }

  svg {
    font-size: 16px;
  }
`;

const WhatsAppButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(37, 211, 102, 0.12);
  border: 1px solid rgba(37, 211, 102, 0.4);
  color: #25d366;
  font-size: 14px;
  font-weight: 600;
  padding: 13px 22px;
  border-radius: 12px;
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    background: #25d366;
    color: #000000;
    box-shadow: 0 6px 20px rgba(37, 211, 102, 0.35);
    transform: translateY(-2px);
  }

  svg {
    font-size: 18px;
  }
`;

const WebsiteButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  padding: 13px 20px;
  border-radius: 12px;
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(133, 76, 230, 0.5);
    color: #e879f9;
  }

  svg {
    font-size: 16px;
  }
`;

const services = [
  {
    icon: <FiLayers />,
    title: "Full Stack Web Development",
    description:
      "End-to-end development of dynamic, high-performance web applications using React, Next.js, Node.js, and Express.",
    tags: ["React.js", "Next.js", "Node.js", "Express.js", "MERN Stack"],
  },
  {
    icon: <FiLayout />,
    title: "Frontend & UI Engineering",
    description:
      "Crafting pixel-perfect, responsive user interfaces with modern typography, smooth micro-interactions, and accessibility.",
    tags: ["Tailwind CSS", "Styled Components", "GSAP", "Responsive UI"],
  },
  {
    icon: <FiServer />,
    title: "RESTful API & Backend Systems",
    description:
      "Architecting secure, scalable backend services, JWT authentication, and seamless third-party API integrations.",
    tags: ["REST APIs", "JWT Auth", "Node.js", "Express", "Postman"],
  },
  {
    icon: <FiDatabase />,
    title: "Database Design & Management",
    description:
      "Structuring and optimizing structured and unstructured databases for reliability, performance, and clean data modeling.",
    tags: ["MongoDB", "MySQL", "PostgreSQL", "Firebase"],
  },
];

const clientOfferings = [
  {
    icon: <FiGlobe />,
    title: "Custom Website Development",
    desc: "Business websites, portfolios, e-commerce, and high-performance web apps built with modern tech.",
  },
  {
    icon: <FiSmartphone />,
    title: "Mobile App Development",
    desc: "Cross-platform Android and iOS applications with seamless UI/UX and native performance.",
  },
  {
    icon: <FiCode />,
    title: "Billing SaaS & Custom Software",
    desc: "Cloud billing software, inventory management, CRM, and tailor-made enterprise web tools.",
  },
  {
    icon: <FiLayers />,
    title: "API & Full Lifecycle Support",
    desc: "WhatsApp CRM automation, payment gateways, third-party APIs, and complete maintenance.",
  },
];

const Services = () => {
  return (
    <Container id="services">
      <Wrapper>
        <Tag>What I Deliver</Tag>
        <Title>Services & Solutions</Title>
        <SubTitle>
          Specialized engineering services focused on building scalable,
          reliable, and modern web products.
        </SubTitle>

        {/* "Need a Website or Mobile App?" Client Call-To-Action Banner */}
        <ClientServicesBanner>
          <PromoHeader>
            <PromoBadge>
              <FiCheck /> Client Services & Tech Startup
            </PromoBadge>

            <PromoTitle>
              Want to Build a <span>Website, Mobile App, or Custom Software</span> for Your Business?
            </PromoTitle>

            <PromoDescription>
              Kya aapko apne business, brand ya startup ke liye <strong>Website, Android/iOS Mobile App ya Custom Billing Software</strong> banwana hai? 
              Hum provide karte hain complete end-to-end development services through <strong>Digital Root Solution</strong> — concept aur UI design se lekar deployment aur maintenance tak.
            </PromoDescription>
          </PromoHeader>

          {/* 4 Quick Offerings */}
          <ClientOfferingsGrid>
            {clientOfferings.map((item, idx) => (
              <OfferingBox key={idx}>
                <OfferingTop>
                  {item.icon}
                  <span>{item.title}</span>
                </OfferingTop>
                <OfferingDesc>{item.desc}</OfferingDesc>
              </OfferingBox>
            ))}
          </ClientOfferingsGrid>

          {/* Direct Actions */}
          <PromoActions>
            <ContactButton href="#contact">
              <FiSend /> Get a Project Quote / Hire Us
            </ContactButton>

            <WhatsAppButton
              href="https://wa.me/919511422002?text=Hello%20Ravi,%20I%20want%20to%20build%20a%20website/app%20for%20my%20business"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaWhatsapp /> Chat on WhatsApp
            </WhatsAppButton>

            <WebsiteButton
              href="https://www.digitalrootsolution.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Visit Digital Root Solution <FiArrowUpRight />
            </WebsiteButton>
          </PromoActions>
        </ClientServicesBanner>

        {/* 4 Core Tech Services Grid */}
        <ServicesGrid>
          {services.map((service, index) => (
            <ServiceCard key={index}>
              <IconBox>{service.icon}</IconBox>
              <CardTitle>{service.title}</CardTitle>
              <CardDescription>{service.description}</CardDescription>
              <TagsContainer>
                {service.tags.map((tag, tIdx) => (
                  <TagBadge key={tIdx}>{tag}</TagBadge>
                ))}
              </TagsContainer>
            </ServiceCard>
          ))}
        </ServicesGrid>
      </Wrapper>
    </Container>
  );
};

export default Services;
